import React, { useState, useEffect, useRef } from "react";
import { Form, Row, Col, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import LocationAutocomplete from "../LocationAutocomplete";
import { userAPI, postalAreaAPI } from "../../api/apiSystem";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import "./HeroSection.css";
import { FaPlus, FaTrash } from "react-icons/fa";

const parseAmountValue = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const normalized = String(value)
    .trim()
    .replace(/[^\d,.-]/g, "");

  if (!normalized) {
    return 0;
  }

  const lastComma = normalized.lastIndexOf(",");
  const lastDot = normalized.lastIndexOf(".");
  const hasComma = lastComma !== -1;
  const hasDot = lastDot !== -1;

  let numeric = normalized;

  if (hasComma && hasDot) {
    if (lastComma > lastDot) {
      numeric = numeric.replace(/\./g, "").replace(",", ".");
    } else {
      numeric = numeric.replace(/,/g, "");
    }
  } else if (hasComma) {
    numeric = numeric.replace(/\./g, "").replace(",", ".");
  } else if (hasDot) {
    numeric = numeric.replace(/,/g, "");
  }

  const parsed = Number(numeric);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatAmountValue = (value) =>
  parseAmountValue(value).toFixed(2).replace(".", ",");

export default function BookingForm() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // ==================== Multiple Locations ====================
  const [pickups, setPickups] = useState([{ id: Date.now(), address: "", lat: null, lng: null, order: 0 }]);
  const [deliveries, setDeliveries] = useState([{ id: Date.now() + 1, address: "", lat: null, lng: null, order: 1 }]);

  // ✅ عداد ترتيب متزايد بيتسجل بيه رقم كل محطة (Pickup أو Delivery) وقت
  // إضافتها فعليًا، عشان لما نبعت للباك إند نحافظ على نفس الترتيب اللي
  // العميل كتبه بالظبط، مش ترتيب افتراضي.
  const orderCounterRef = useRef(2);

  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isValidPrice, setIsValidPrice] = useState(false);

  // تحميل السيارات
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const res = await postalAreaAPI.index();
        if (res.success && Array.isArray(res.data?.data)) {
          setVehicles(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching vehicles:", err);
      }
    };
    fetchVehicles();
  }, []);

  // ==================== Multi Location Handlers ====================
  const addPickup = () => setPickups([...pickups, { id: Date.now(), address: "", lat: null, lng: null, order: orderCounterRef.current++ }]);
  const addDelivery = () => setDeliveries([...deliveries, { id: Date.now(), address: "", lat: null, lng: null, order: orderCounterRef.current++ }]);

  const removePickup = (id) => {
    if (pickups.length === 1) return;
    setPickups(pickups.filter(p => p.id !== id));
  };

  const removeDelivery = (id) => {
    if (deliveries.length === 1) return;
    setDeliveries(deliveries.filter(d => d.id !== id));
  };

  const handlePickupChange = (index, address, lat = null, lng = null) => {
    const updated = [...pickups];
    updated[index] = { ...updated[index], address, lat, lng };
    setPickups(updated);
  };

  const handleDeliveryChange = (index, address, lat = null, lng = null) => {
    const updated = [...deliveries];
    updated[index] = { ...updated[index], address, lat, lng };
    setDeliveries(updated);
  };

  /**
   * ✅ بيبني مصفوفة "stops" مرتبة بنفس ترتيب إدخال العميل الحقيقي (بغض
   * النظر عن كون المحطة Pickup أو Delivery)، باستخدام رقم الـ order اللي
   * اتسجل وقت إضافة/تعديل كل محطة.
   */
  const getOrderedStops = () => {
    const pickupStops = pickups
      .map((p, idx) => ({ p, idx }))
      .filter(({ p }) => p.address?.trim())
      .map(({ p, idx }) => ({
        type: "pickup",
        address: p.address.trim(),
        lat: p.lat,
        lng: p.lng,
        order: p.order ?? idx * 2,
      }));

    const deliveryStops = deliveries
      .map((d, idx) => ({ d, idx }))
      .filter(({ d }) => d.address?.trim())
      .map(({ d, idx }) => ({
        type: "dropoff",
        address: d.address.trim(),
        lat: d.lat,
        lng: d.lng,
        order: d.order ?? idx * 2 + 1,
      }));

    return [...pickupStops, ...deliveryStops]
      .sort((a, b) => a.order - b.order)
      .map(({ type, address, lat, lng }) => ({ type, address, lat, lng }));
  };

  // ==================== Price Calculation (فقط بعد اختيار السيارة) ====================
  useEffect(() => {
    if (
      !selectedVehicle ||
      !pickups.every(
        (pickup) =>
          pickup.address?.trim() &&
          pickup.lat != null &&
          pickup.lng != null,
      ) ||
      !deliveries.every(
        (delivery) =>
          delivery.address?.trim() &&
          delivery.lat != null &&
          delivery.lng != null,
      )
    ) {
      setPrice(null);
      setIsValidPrice(false);
      return;
    }

    const fetchPrice = async () => {
      setLoading(true);
      setError("");

      try {
        const stops = getOrderedStops();
        const dataToSend = {
          vehicle_id: selectedVehicle,
          stops,
          booking_from: pickups.map(p => p.address),
          booking_to: deliveries.map(d => d.address),
          from_lat_long: pickups.map(p => ({ lat: p.lat, lng: p.lng })),
          to_lat_long: deliveries.map(d => ({ lat: d.lat, lng: d.lng })),
        };

        const res = await userAPI.bookings.calculateHome(dataToSend);

        // console.log('res-res', res.raw.is_active);

        if (res.success && res.data?.data?.sub_total) {
          setPrice(res.data.data.sub_total);
          setIsValidPrice(true);
          setError("");
        } else if (!res.raw.is_active) {
          setIsValidPrice(false);
          setError(t("unavailable"));
        } else {
          setIsValidPrice(false);
          setError(t("price.errors.unableToCalculate"));
        }
      } catch (err) {
        console.error(err);
        setIsValidPrice(false);
        setError(t("price.errors.calculationError"));
      } finally {
        setLoading(false);
      }
    };

    fetchPrice();
  }, [pickups, deliveries, selectedVehicle, t]);

  // ==================== Submit ====================
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedVehicle) {
      setError("⚠️ يرجى اختيار نوع السيارة");
      return;
    }

    const stops = getOrderedStops();

    const bookingData = {
      pickups: pickups.map(p => ({ address: p.address, lat: p.lat, lng: p.lng })),
      deliveries: deliveries.map(d => ({ address: d.address, lat: d.lat, lng: d.lng })),
      // ✅ الترتيب الحقيقي اللي كتبه العميل، عشان صفحة BookingPage تكمل بيه
      stops,
      vehicle_id: selectedVehicle,
      price,
    };

    localStorage.setItem("bookingData", JSON.stringify(bookingData));
    navigate("/bookings");
  };

  const isButtonDisabled = loading || !isValidPrice || !selectedVehicle ||
    pickups.some(p => !p.address?.trim()) || deliveries.some(d => !d.address?.trim());

  return (
    <Form className="quote-form" onSubmit={handleSubmit}>
      <div className="text-start mb-5">
        <h2 className="text-dark fw-bold fs-4 ">{t("bookingForm.title")}</h2>
        <p className="text-muted mb-0">{t("bookingForm.subtitle")}</p>
      </div>

      <Row className="g-3 gx-2 gy-3 justify-content-center">
        {/* من */}
        <Col lg={4} md={6} xs={12}>
          <Form.Group controlId="formFrom">
            <div className="d-flex justify-content-between align-items-center mb-3 me-4">
              <Form.Label className="fw-semibold mb-2 text-muted">
                {t("bookingForm.pickupLabel")}
              </Form.Label>
            </div>

            {pickups.map((pickup, index) => (
              <Row key={pickup.id} className="align-items-baseline g-2">
                <Col xs={10} md={10} className="pe-0 mb-0">
                  <LocationAutocomplete
                    value={pickup.address}
                    onChange={(e) =>
                      handlePickupChange(
                        index,
                        e.target.value,
                        e.target.lat ?? null,
                        e.target.lng ?? null,
                      )
                    }
                    onLocationSelect={(_, address, lat, lng) => 
                      handlePickupChange(index, address, lat, lng)
                    }
                    placeholder={t("enter_address")}
                  />
                </Col>
                <Col xs={2} md={2} className="ps-1 py-0">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removePickup(pickup.id)}
                    disabled={pickups.length === 1}
                    style={{ 
                      fontSize: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FaTrash />
                  </Button>
                </Col>
              </Row>
            ))}

            <div className="me-3">
              <Button
                variant="outline-success"
                size="sm"
                onClick={addPickup}
                className="w-md-auto"
                style={{ fontSize: '10px' }}
              >
                <FaPlus className="me-1" /> {t("add")}
              </Button>
            </div>
          </Form.Group>
        </Col>

        {/* إلى */}
        <Col lg={4} md={6} xs={12}>
          <Form.Group controlId="formTo">
            <div className="d-flex justify-content-between align-items-center mb-3 me-4">
              <Form.Label className="fw-semibold mb-2 text-muted">
                {t("bookingForm.deliveryLabel")}
              </Form.Label>
            </div>

            {deliveries.map((delivery, index) => (
              <Row key={delivery.id} className="align-items-baseline g-2">
                <Col xs={10} md={10} className="pe-0 mb-0">
                  <LocationAutocomplete
                    value={delivery.address}
                    onChange={(e) =>
                      handleDeliveryChange(
                        index,
                        e.target.value,
                        e.target.lat ?? null,
                        e.target.lng ?? null,
                      )
                    }
                    onLocationSelect={(_, address, lat, lng) => handleDeliveryChange(index, address, lat, lng)}
                    // placeholder={`${t("bookingForm.deliveryPlaceholder")} ${index + 1}`}
                    placeholder={t("enter_address")}
                  />
                </Col>
                <Col xs={2} md={2} className="ps-1 py-0">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeDelivery(delivery.id)} 
                    disabled={deliveries.length === 1}
                    style={{ 
                      fontSize: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FaTrash />
                  </Button>
                </Col>
              </Row>
            ))}

            <div className="me-3">
              <Button
                variant="outline-success"
                size="sm"
                onClick={addDelivery}
                className="w-md-auto"
                style={{ fontSize: '10px' }}
              >
                <FaPlus className="me-1" /> {t("add")}
              </Button>
            </div>
          </Form.Group>
        </Col>

        {/* نوع المركبة */}
        <Col lg={4} md={6} xs={12} className="py-3">
          <Form.Group controlId="formVehicle">
            <Form.Label className="fw-semibold mb-2 text-muted">
              {t("bookingForm.vehicleLabel")}
            </Form.Label>
            <Select
              options={vehicles.map(v => ({
                value: v.id,
                label: v.is_active === 1 || v.is_active === "1"
                  ? `${v.size || v.name} ${v.weight ? `(${v.weight}kg)` : ""}`
                  : `${v.size || v.name} ${v.weight ? `(${v.weight}kg)` : ""} ${t("unavailable")}`,
                isDisabled: v.is_active !== 1 && v.is_active !== "1",
              }))}
              onChange={(selected) => setSelectedVehicle(selected?.value)}
              placeholder={t("general.selectVehicleType")}
              className="text-dark"
              isOptionDisabled={(option) => option.isDisabled}
            />
          </Form.Group>
        </Col>

        {/* الرسائل والزر */}
        <Col lg={8} xs={12}>
          <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 p-3 bg-light rounded">
            <div className="text-center text-md-start">
              {isValidPrice && price ? (
                <div className="mb-2 ">
                  <strong className="text-success d-flex flex-column align-items-start fs-5">
                    {t("general.estimatedPrice")}: {price} € {t("Netto")}
                    <span className="text-muted fs-6">
                      {" "}
                      zzgl. 19 % MwSt. ={" "}
                      {formatAmountValue(parseAmountValue(price) * 1.19)}{" "}
                      € {t("Brutto")}
                    </span>
                  </strong>
                </div>
              ) : error ? (
                <div
                  className=" text-danger"
                  dangerouslySetInnerHTML={{ __html: error }}
                />
              ) : null}
              <small className="text-muted">
                {t("pickup_locations")}{" "}
                <a href="/loading-city" className="text-orange">
                  {t("here")}
                </a>
              </small>
            </div>

            <Button
              type="submit"
              variant={isValidPrice ? "warning" : "secondary"}
              size="lg"
              className="bg-orange-500 text-white flex-shrink-0 px-4"
              style={{ minWidth: "200px" }}
              disabled={isButtonDisabled}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Calculating...
                </>
              ) : isValidPrice ? (
                `${t("confirmEmail.confirm_button")} (${price} €)`
              ) : (
                t("general.calculatePrice")
              )}
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  );
}